import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plane, Loader2 } from 'lucide-react';
import { getToken } from '../../utils/tokenManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
];

/**
 * AwayCheckinCard — self-serve "I'll be away" card.
 *
 * Lets Continuity System members push their next check-in out by 30, 60
 * or 90 days without freezing their whole vault or calling support.
 * Only renders when the member has an active vault with no open reminder.
 *
 * Backend contract:
 *   POST /api/estate-vault/pause-checkin  { days: 30|60|90 }
 *     200 → { ok, next_checkin_due_at, days }
 *     404 → no active vault (silently hides the card)
 *     409 → cascade already open (silently hides the card)
 */
const AwayCheckinCard = () => {
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // days being saved

  const loadVault = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/estate-vault`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVault(res.data || null);
    } catch {
      setVault(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVault(); }, []);

  const onExtend = async (days) => {
    setSaving(days);
    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/estate-vault/pause-checkin`,
        { days },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newDue = new Date(res.data.next_checkin_due_at);
      toast.success(`We'll wait ${days} days before the next check-in.`, {
        description: `Your next check-in is now due ${newDue.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.`,
      });
      await loadVault();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not extend your check-in. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(null);
    }
  };

  // Only show when the member actually has an active vault with no open reminder
  if (loading) return null;
  if (!vault || vault.status !== 'active') return null;
  if ((vault.dms_stage || 0) >= 1) return null;

  const nextDue = vault.next_checkin_due_at
    ? new Date(vault.next_checkin_due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <Card data-testid="away-checkin-card" className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-emerald-600" aria-hidden="true" />
          <span>I&apos;ll be away</span>
        </CardTitle>
        <CardDescription>
          Going off-grid for a while? Push your next check-in out so we don&apos;t nudge you while you&apos;re travelling.
          {nextDue && (
            <span className="block mt-1 text-slate-600">
              Next check-in due: <span className="font-medium text-slate-800">{nextDue}</span>
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {OPTIONS.map((opt) => (
            <Button
              key={opt.days}
              variant="outline"
              size="sm"
              onClick={() => onExtend(opt.days)}
              disabled={saving !== null}
              data-testid={`away-checkin-${opt.days}`}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            >
              {saving === opt.days && <Loader2 className="w-3 h-3 animate-spin mr-1.5" />}
              +{opt.label}
            </Button>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          You can stack these up. If a reminder is already open, respond to that first.
        </p>
      </CardContent>
    </Card>
  );
};

export default AwayCheckinCard;
